/**
 * RBAC (Role-Based Access Control) System
 * Defines roles, permissions, and access control utilities
 */

// Permission definitions
export const PERMISSIONS = {
  // Finance permissions
  VIEW_FINANCE: 'view_finance',
  MANAGE_INCOME: 'manage_income',
  MANAGE_EXPENSES: 'manage_expenses',
  VIEW_REPORTS: 'view_reports',
  
  // Choir permissions
  VIEW_CHOIR: 'view_choir',
  MANAGE_CHOIR_MEMBERS: 'manage_choir_members',
  MANAGE_REHEARSALS: 'manage_rehearsals',
  MANAGE_HYMNS: 'manage_hymns',
  
  // Communications permissions
  VIEW_COMMUNICATIONS: 'view_communications',
  MANAGE_ANNOUNCEMENTS: 'manage_announcements',
  MANAGE_MESSAGES: 'manage_messages',
  SEND_EMAILS: 'send_emails',
  
  // Shepherd permissions
  VIEW_SHEPHERD: 'view_shepherd',
  SEND_BROADCASTS: 'send_broadcasts',
  
  // Media permissions
  VIEW_MEDIA: 'view_media',
  MANAGE_WEBCAST: 'manage_webcast',
  MANAGE_FLYERS: 'manage_flyers',
  MANAGE_GALLERY: 'manage_gallery',
  
  // Usher permissions
  VIEW_USHER: 'view_usher',
  MANAGE_ATTENDANCE: 'manage_attendance',
  
  // Sunday School permissions
  VIEW_SUNDAY_SCHOOL: 'view_sunday_school',
  MANAGE_SYLLABUS: 'manage_syllabus',
  MANAGE_TEACHERS: 'manage_teachers',
  
  // Secretarial permissions
  VIEW_SECRETARIAL: 'view_secretarial',
  MANAGE_MEMBERS: 'manage_members',
  MANAGE_SERVICES: 'manage_services',
  MANAGE_ATTACHMENTS: 'manage_attachments',
  MANAGE_ENROLLMENTS: 'manage_enrollments',
  
  // Admin permissions
  MANAGE_ADMIN_USERS: 'manage_admin_users',
  VIEW_AUDIT_LOGS: 'view_audit_logs',
  MANAGE_SETTINGS: 'manage_settings',
  
  // Harvest/Projects
  VIEW_HARVEST: 'view_harvest',
  MANAGE_HARVEST: 'manage_harvest',
  
  // Rules & Tenets permissions
  VIEW_RULES: 'view_rules',
  MANAGE_RULES: 'manage_rules',
  
  // Rota permissions
  VIEW_ROTA: 'view_rota',
  MANAGE_ROTA: 'manage_rota',
  MANAGE_MINISTERS: 'manage_ministers',
  
  // Bible Lessons permissions
  VIEW_BIBLE_LESSONS: 'view_bible_lessons',
  MANAGE_BIBLE_LESSONS: 'manage_bible_lessons',
  
  // Volunteer permissions
  VIEW_VOLUNTEER: 'view_volunteer',
  MANAGE_VOLUNTEER: 'manage_volunteer'
};

// Role definitions with default permissions
export const ROLES = {
  main_admin: {
    name: 'Main Admin',
    description: 'Full system access',
    permissions: Object.values(PERMISSIONS) // All permissions
  },
  finance_admin: {
    name: 'Finance Admin',
    description: 'Manage church finances',
    permissions: [
      PERMISSIONS.VIEW_FINANCE,
      PERMISSIONS.MANAGE_INCOME,
      PERMISSIONS.MANAGE_EXPENSES,
      PERMISSIONS.VIEW_REPORTS,
      PERMISSIONS.VIEW_HARVEST,
      PERMISSIONS.MANAGE_HARVEST,
      PERMISSIONS.SEND_EMAILS
    ]
  },
  choir_admin: {
    name: 'Choir Admin',
    description: 'Manage choir ministry',
    permissions: [
      PERMISSIONS.VIEW_CHOIR,
      PERMISSIONS.MANAGE_CHOIR_MEMBERS,
      PERMISSIONS.MANAGE_REHEARSALS,
      PERMISSIONS.MANAGE_HYMNS,
      PERMISSIONS.SEND_EMAILS
    ]
  },
  communications_admin: {
    name: 'Communications Admin',
    description: 'Manage church communications',
    permissions: [
      PERMISSIONS.VIEW_COMMUNICATIONS,
      PERMISSIONS.MANAGE_ANNOUNCEMENTS,
      PERMISSIONS.MANAGE_MESSAGES,
      PERMISSIONS.SEND_EMAILS
    ]
  },
  media_admin: {
    name: 'Media Admin',
    description: 'Manage media and webcast',
    permissions: [
      PERMISSIONS.VIEW_MEDIA,
      PERMISSIONS.MANAGE_WEBCAST,
      PERMISSIONS.MANAGE_FLYERS,
      PERMISSIONS.MANAGE_GALLERY,
      PERMISSIONS.SEND_EMAILS
    ]
  },
  usher_admin: {
    name: 'Usher Admin',
    description: 'Manage attendance tracking',
    permissions: [
      PERMISSIONS.VIEW_USHER,
      PERMISSIONS.MANAGE_ATTENDANCE,
      PERMISSIONS.SEND_EMAILS
    ]
  },
  sunday_school_admin: {
    name: 'Sunday School Admin',
    description: 'Manage Sunday School',
    permissions: [
      PERMISSIONS.VIEW_SUNDAY_SCHOOL,
      PERMISSIONS.MANAGE_SYLLABUS,
      PERMISSIONS.MANAGE_TEACHERS,
      PERMISSIONS.SEND_EMAILS
    ]
  },
  secretary_admin: {
    name: 'Secretary Admin',
    description: 'Manage church records',
    permissions: [
      PERMISSIONS.VIEW_SECRETARIAL,
      PERMISSIONS.MANAGE_MEMBERS,
      PERMISSIONS.MANAGE_SERVICES,
      PERMISSIONS.MANAGE_ATTACHMENTS,
      PERMISSIONS.MANAGE_ENROLLMENTS,
      PERMISSIONS.SEND_EMAILS
    ]
  },
  shepherd: {
    name: 'Shepherd',
    description: 'Church shepherd access',
    permissions: [
      PERMISSIONS.VIEW_SHEPHERD,
      PERMISSIONS.SEND_BROADCASTS,
      PERMISSIONS.VIEW_FINANCE,
      PERMISSIONS.VIEW_REPORTS,
      PERMISSIONS.VIEW_COMMUNICATIONS,
      PERMISSIONS.SEND_EMAILS
    ]
  },
  viewer: {
    name: 'Viewer',
    description: 'Read-only access',
    permissions: [
      PERMISSIONS.VIEW_FINANCE,
      PERMISSIONS.VIEW_CHOIR,
      PERMISSIONS.VIEW_COMMUNICATIONS,
      PERMISSIONS.VIEW_MEDIA,
      PERMISSIONS.VIEW_USHER,
      PERMISSIONS.VIEW_SUNDAY_SCHOOL,
      PERMISSIONS.VIEW_SECRETARIAL,
      PERMISSIONS.VIEW_HARVEST
    ]
  }
};

// Department to permissions mapping
export const DEPARTMENT_PERMISSIONS = {
  finance: [PERMISSIONS.VIEW_FINANCE, PERMISSIONS.MANAGE_INCOME, PERMISSIONS.MANAGE_EXPENSES, PERMISSIONS.VIEW_REPORTS],
  choir: [PERMISSIONS.VIEW_CHOIR, PERMISSIONS.MANAGE_CHOIR_MEMBERS, PERMISSIONS.MANAGE_REHEARSALS, PERMISSIONS.MANAGE_HYMNS],
  shepherd: [PERMISSIONS.VIEW_SHEPHERD, PERMISSIONS.SEND_BROADCASTS],
  harvest: [PERMISSIONS.VIEW_HARVEST, PERMISSIONS.MANAGE_HARVEST],
  projects: [PERMISSIONS.VIEW_HARVEST, PERMISSIONS.MANAGE_HARVEST], // Alias for harvest/church projects
  communications: [PERMISSIONS.VIEW_COMMUNICATIONS, PERMISSIONS.MANAGE_ANNOUNCEMENTS, PERMISSIONS.MANAGE_MESSAGES],
  email: [PERMISSIONS.SEND_EMAILS],
  usher: [PERMISSIONS.VIEW_USHER, PERMISSIONS.MANAGE_ATTENDANCE],
  sundayschool: [PERMISSIONS.VIEW_SUNDAY_SCHOOL, PERMISSIONS.MANAGE_SYLLABUS, PERMISSIONS.MANAGE_TEACHERS],
  media: [PERMISSIONS.VIEW_MEDIA, PERMISSIONS.MANAGE_WEBCAST, PERMISSIONS.MANAGE_FLYERS, PERMISSIONS.MANAGE_GALLERY],
  secretarial: [PERMISSIONS.VIEW_SECRETARIAL, PERMISSIONS.MANAGE_MEMBERS, PERMISSIONS.MANAGE_SERVICES, PERMISSIONS.MANAGE_ATTACHMENTS, PERMISSIONS.MANAGE_ENROLLMENTS],
  secretary: [PERMISSIONS.VIEW_SECRETARIAL, PERMISSIONS.MANAGE_MEMBERS, PERMISSIONS.MANAGE_SERVICES, PERMISSIONS.MANAGE_ATTACHMENTS, PERMISSIONS.MANAGE_ENROLLMENTS], // Alias
  admin_users: [PERMISSIONS.MANAGE_ADMIN_USERS],
  audit_logs: [PERMISSIONS.VIEW_AUDIT_LOGS],
  settings: [PERMISSIONS.MANAGE_SETTINGS],
  rules: [PERMISSIONS.VIEW_RULES, PERMISSIONS.MANAGE_RULES], // Rules & Tenets
  rota: [PERMISSIONS.VIEW_ROTA, PERMISSIONS.MANAGE_ROTA, PERMISSIONS.MANAGE_MINISTERS], // Church Rota
  announcements: [PERMISSIONS.VIEW_COMMUNICATIONS, PERMISSIONS.MANAGE_ANNOUNCEMENTS], // Announcements
  analytics: [PERMISSIONS.VIEW_FINANCE, PERMISSIONS.VIEW_REPORTS], // Analytics
  biblelessons: [PERMISSIONS.VIEW_BIBLE_LESSONS, PERMISSIONS.MANAGE_BIBLE_LESSONS], // Bible Lessons
  volunteer: [PERMISSIONS.VIEW_VOLUNTEER, PERMISSIONS.MANAGE_VOLUNTEER] // Volunteer Attendance
};

/**
 * Check if a user has a specific permission
 */
export const hasPermission = (user, permission) => {
  if (!user) return false;
  
  // Main admin has all permissions
  if (user.role === 'main_admin') return true;
  
  // Check user's explicit permissions
  if (user.permissions && user.permissions.includes(permission)) return true;
  
  // Check role-based permissions
  const roleConfig = ROLES[user.role];
  if (roleConfig && roleConfig.permissions.includes(permission)) return true;
  
  return false;
};

/**
 * Check if a user has any of the specified permissions
 */
export const hasAnyPermission = (user, permissions) => {
  return permissions.some(permission => hasPermission(user, permission));
};

/**
 * Check if a user has all of the specified permissions
 */
export const hasAllPermissions = (user, permissions) => {
  return permissions.every(permission => hasPermission(user, permission));
};

/**
 * Check if a user can access a specific department
 */
export const canAccessDepartment = (user, department) => {
  if (!user) return false;
  if (user.role === 'main_admin') return true;
  
  // Check if user has the department ID directly in their permissions
  if (user.permissions && user.permissions.includes(department)) {
    return true;
  }
  
  const deptPermissions = DEPARTMENT_PERMISSIONS[department];
  if (!deptPermissions) return false;
  
  // User needs at least view permission for the department
  return hasAnyPermission(user, deptPermissions);
};

/**
 * Get all accessible departments for a user
 */
export const getAccessibleDepartments = (user) => {
  if (!user) return [];
  if (user.role === 'main_admin') return Object.keys(DEPARTMENT_PERMISSIONS);
  
  return Object.keys(DEPARTMENT_PERMISSIONS).filter(dept => 
    canAccessDepartment(user, dept)
  );
};

/**
 * Get role display name
 */
export const getRoleName = (roleKey) => {
  return ROLES[roleKey]?.name || roleKey;
};

/**
 * Get all available roles for user creation
 */
export const getAvailableRoles = () => {
  return Object.entries(ROLES).map(([key, config]) => ({
    value: key,
    label: config.name,
    description: config.description
  }));
};

export default {
  PERMISSIONS,
  ROLES,
  DEPARTMENT_PERMISSIONS,
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  canAccessDepartment,
  getAccessibleDepartments,
  getRoleName,
  getAvailableRoles
};
